import { useEffect, useState } from 'react'
import { useCart } from '../../../../cart'
import { purchasabilityMessageByReason } from '../../../purchasability'
import type { CatalogProduct, CatalogProductPurchasability } from '../../../types'

export const useProductCartActions = ({
  productData,
  productLookup,
  purchasability,
}: {
  productData: CatalogProduct | null
  productLookup: string
  purchasability: CatalogProductPurchasability
}) => {
  const { addItem, itemCount, busy: cartBusy, clearError, getLastError } = useCart()
  const [cartQuantity, setCartQuantity] = useState(1)
  const [cartMessage, setCartMessage] = useState('')
  const [cartMessageTone, setCartMessageTone] = useState<'success' | 'error' | null>(null)

  const availableQuantity = purchasability.availableStock
  const maxSelectableQuantity =
    purchasability.stockPolicy === 'allow-backorder' ? 20 : Math.max(1, availableQuantity)

  useEffect(() => {
    setCartQuantity((current) => Math.max(1, Math.min(current, maxSelectableQuantity)))
  }, [maxSelectableQuantity])

  const handleIncreaseCartQuantity = () => {
    setCartQuantity((current) => Math.min(maxSelectableQuantity, current + 1))
  }

  const handleDecreaseCartQuantity = () => {
    setCartQuantity((current) => Math.max(1, current - 1))
  }

  const handleAddToCart = async () => {
    if (!productData?._id || !productLookup.trim()) {
      setCartMessage('Prodotto non disponibile')
      setCartMessageTone('error')
      return
    }
    if (!purchasability.isPurchasable) {
      const reason = purchasability.reason
      setCartMessage(reason ? purchasabilityMessageByReason[reason] : 'Prodotto non acquistabile')
      setCartMessageTone('error')
      return
    }
    const maxQuantity =
      purchasability.stockPolicy === 'allow-backorder' ? 20 : Math.max(1, availableQuantity)
    const quantity = Math.max(1, Math.min(maxQuantity, cartQuantity))
    const ok = await addItem(productLookup, quantity)
    if (ok) {
      setCartMessage('Prodotto aggiunto al carrello')
      setCartMessageTone('success')
      clearError()
      return
    }
    const detailedMessage = getLastError().trim()
    setCartMessage(detailedMessage || 'Impossibile aggiungere il prodotto al carrello')
    setCartMessageTone('error')
  }

  return {
    itemCount,
    cartBusy,
    cartQuantity,
    cartMessage,
    cartMessageTone,
    handleIncreaseCartQuantity,
    handleDecreaseCartQuantity,
    handleAddToCart,
  }
}
